import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { AiOutlineUserAdd } from "react-icons/ai";
import { BiEdit } from "react-icons/bi";
import { FaToggleOff } from "react-icons/fa";
import { FiMinusCircle } from "react-icons/fi";
import ReactLoading from "react-loading";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchBannerAds,
  deleteBannerAd,
  updateBannerAd,
} from "utils/services/bannerAd.services";

const BannerAds = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNoDataText, setShowNoDataText] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteAdId, setDeleteAdId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusAdId, setStatusAdId] = useState(null);
  const [isActivating, setIsActivating] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);

  const navigate = useNavigate();

  const showDeleteConfirmationModal = (id) => {
    document.body.classList.add("modal-open");
    setDeleteAdId(id);
    setShowDeleteConfirmation(true);
  };

  const hideDeleteConfirmationModal = () => {
    document.body.classList.remove("modal-open");
    setShowDeleteConfirmation(false);
    setDeleteAdId(null);
  };

  const showStatusConfirmationModal = (id, currentStatus) => {
    document.body.classList.add("modal-open");
    setStatusAdId(id);
    setTargetStatus(!currentStatus);
    setShowStatusConfirmation(true);
  };

  const hideStatusConfirmationModal = () => {
    document.body.classList.remove("modal-open");
    setShowStatusConfirmation(false);
    setStatusAdId(null);
    setTargetStatus(null);
  };

  const fetchAllBannerAds = async () => {
    setLoading(true);
    try {
      const response = await fetchBannerAds(pageNumber, pageSize);
      if (response) {
        setData(response.data || []);
        setTotalRowCount(response.data.totalCount || 0);
        setShowNoDataText(!response.data?.length);
      }
    } catch (error) {
      console.error("Error fetching banner ads:", error);
      toast.error(error.message || "Failed to fetch banner ads");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllBannerAds();
  }, [pageNumber, pageSize]);

  const handleEdit = (row) => {
    navigate(`/admin/banner-ad/${row.id}`);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteBannerAd(deleteAdId);
      toast.success("Banner ad deleted successfully");
      fetchAllBannerAds();
      hideDeleteConfirmationModal();
    } catch (error) {
      toast.error(error.message || "Failed to delete banner ad");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async () => {
    setIsActivating(true);
    try {
      await updateBannerAd(statusAdId, { isActive: targetStatus });
      toast.success(
        `Banner ad ${targetStatus ? "activated" : "deactivated"} successfully`
      );
      fetchAllBannerAds();
      hideStatusConfirmationModal();
    } catch (error) {
      toast.error(error.message || "Failed to update banner ad status");
    } finally {
      setIsActivating(false);
    }
  };

  const openModal = () => {
    navigate("/admin/banner-ad/new");
  };

  const paginationOptions = {
    rowsPerPageText: "Items Per Page",
    rangeSeparatorText: "of",
    selectAllRowsItem: true,
    selectAllRowsItemText: "All",
  };

  const columns = [
    {
      name: "Title",
      selector: (row) => row.title || "-",
      sortable: true,
    },
    {
      name: "Image",
      cell: (row) => {
        if (row.imageUrl) {
          return (
            <img
              src={row.imageUrl}
              alt={row.title || "Banner Ad"}
              className="h-16 w-16 object-cover rounded"
            />
          );
        }
        return <span>-</span>;
      },
      sortable: false,
    },
    {
      name: "Website Link",
      selector: (row) => row.redirectUrl || "-",
      cell: (row) => (
        <a
          href={row.redirectUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline truncate max-w-xs block"
        >
          {row.redirectUrl || "-"}
        </a>
      ),
      sortable: false,
    },
    {
      name: "Status",
      selector: (row) => (row.isActive ? "Active" : "Inactive"),
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded text-xs font-semibold ${row.isActive
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
            }`}
        >
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => handleEdit(row)}
              className="bg-theme cursor-pointer rounded px-4 py-2 font-bold text-white hover:bg-blue-700 h-10 mx-auto"
            >
              <BiEdit />
            </button>
            <button
              onClick={() => showStatusConfirmationModal(row.id, row.isActive)}
              className={`rounded px-4 py-2 font-bold text-white h-10 mx-auto flex items-center gap-2 ${row.isActive
                  ? "bg-orange-500 hover:bg-orange-600"
                  : "bg-green-500 hover:bg-green-600"
                }`}
            >
              <span className="hidden md:inline">
                {row.isActive ? "Deactivate" : "Activate"}
              </span>
              <span className="inline md:hidden">
                <FaToggleOff />
              </span>
            </button>
            <button
              onClick={() => showDeleteConfirmationModal(row.id)}
              className="hover-bg-red-700 cursor-pointer rounded bg-red-500 px-4 py-2 font-bold text-white h-10 mx-auto"
            >
              <FiMinusCircle />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="mt-3">
      <div className="flex justify-end">
        <button
          onClick={openModal}
          className="flex items-center rounded-xl bg-[#4318FF] px-5 py-2 text-white hover:bg-blue-600 focus:outline-none"
        >
          <AiOutlineUserAdd className="mr-2 h-6 w-6" />
          Add Banner Ad
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-gray-900 bg-opacity-50">
          <div className="rounded bg-white p-6">
            <p>Are you sure you want to delete this ad?</p>
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="mr-2 rounded bg-red-500 px-4 py-2 text-white"
              >
                {isDeleting ? "Deleting..." : "Yes"}
              </button>
              <button
                onClick={hideDeleteConfirmationModal}
                disabled={isDeleting}
                className="rounded bg-gray-500 px-4 py-2 text-white"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Toggle Confirmation Modal */}
      {showStatusConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-gray-900 bg-opacity-50">
          <div className="rounded bg-white p-6">
            <p>
              Are you sure you want to{" "}
              {targetStatus ? "activate" : "inactivate"} this ad?
            </p>
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleToggleStatus}
                disabled={isActivating}
                className="mr-2 rounded bg-red-500 px-4 py-2 text-white"
              >
                {isActivating
                  ? targetStatus
                    ? "Activating..."
                    : "Deactivating..."
                  : "Yes"}
              </button>
              <button
                onClick={hideStatusConfirmationModal}
                disabled={isActivating}
                className="rounded bg-gray-500 px-4 py-2 text-white"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 overflow-x-auto rounded-lg bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={data}
          pagination
          progressPending={loading}
          paginationComponentOptions={paginationOptions}
          progressComponent={
            <ReactLoading
              type={"bubbles"}
              color={"#4318FF"}
              height={100}
              width={100}
            />
          }
          noDataComponent={
            showNoDataText ? (
              <div className="py-8 text-center text-gray-500">
                There are no records to display
              </div>
            ) : (
              <ReactLoading
                type={"bubbles"}
                color={"#4318FF"}
                height={100}
                width={100}
              />
            )
          }
          onChangePage={(value) => {
            setPageNumber(value);
          }}
          paginationServer
          onChangeRowsPerPage={(value) => {
            setPageSize(value);
            setPageNumber(1);
          }}
          paginationTotalRows={totalRowCount}
          paginationDefaultPage={pageNumber}
          customStyles={{
            rows: {
              style: {
                minHeight: "2.5rem",
              },
            },
            headCells: {
              style: {
                fontSize: "0.9rem",
                backgroundColor: "#4318FF",
                fontWeight: "bold",
                letterSpacing: "0.05rem",
                color: "#FFFFFF",
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
              },
            },
            cells: {
              style: {
                fontSize: "0.9rem",
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
              },
            },
            pagination: {
              style: {
                borderTop: "1px solid #e5e7eb",
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default BannerAds;

